import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, LogIn, UserPlus, ChevronLeft, Trophy, Users, DollarSign,
  Target, Package, PieChart, Send, Search, Filter, TrendingUp, Award,
  Check, X, Star, Shield, Globe, Eye, ArrowRight, Briefcase
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_LABELS = { tier_5: 'Champion', tier_4: 'High Performance', tier_3: 'Elite', tier_2: 'Performance', tier_1: 'Development' };
const TIER_COLORS = {
  tier_5: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  tier_4: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  tier_3: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  tier_2: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  tier_1: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};
const CATEGORIES = ['apparel', 'footwear', 'equipment', 'nutrition', 'technology', 'travel'];

export default function SponsorshipPortal() {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState('login');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [corporateToken, setCorporateToken] = useState(localStorage.getItem('corporate_token') || '');
  const [corporate, setCorporate] = useState(null);
  const [tab, setTab] = useState('dashboard');

  // Data states
  const [packages, setPackages] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [offers, setOffers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Forms
  const [loginForm, setLoginForm] = useState({ contact_email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({
    company_name: '', country: 'Kenya', industry: '', contact_person: '',
    contact_email: '', contact_phone: '', website: '', password: ''
  });
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [offerForm, setOfferForm] = useState({ category: '', proposed_value: '', currency_code: 'KES', message: '', duration_months: 12 });
  const [showAllocateDialog, setShowAllocateDialog] = useState(false);
  const [allocForm, setAllocForm] = useState([{ sport: '', allocated_amount: '', currency_code: 'KES' }]);
  const [sportFilter, setSportFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${corporateToken}` };

  // Check existing token
  useEffect(() => {
    const token = localStorage.getItem('corporate_token');
    if (token) {
      setCorporateToken(token);
      loadProfile(token);
    }
  }, []);

  const loadProfile = async (token) => {
    try {
      const res = await axios.post(`${API_URL}/api/sponsorship/corporates/login`, loginForm).catch(() => null);
      // Try to validate token by fetching data
      setIsAuthenticated(true);
      loadDashboardData(token);
    } catch {
      localStorage.removeItem('corporate_token');
      setIsAuthenticated(false);
    }
  };

  const loadDashboardData = useCallback(async (token) => {
    const h = { Authorization: `Bearer ${token || corporateToken}` };
    setLoading(true);
    try {
      const [pkgRes, currRes, offersRes, contractsRes, allocRes] = await Promise.all([
        axios.get(`${API_URL}/api/sponsorship/packages`),
        axios.get(`${API_URL}/api/sponsorship/currencies`),
        axios.get(`${API_URL}/api/sponsorship/offers/me`, { headers: h }).catch(() => ({ data: { offers: [] } })),
        axios.get(`${API_URL}/api/sponsorship/contracts`, { headers: h }).catch(() => ({ data: { contracts: [] } })),
        axios.get(`${API_URL}/api/sponsorship/allocations/me`, { headers: h }).catch(() => ({ data: { allocations: [] } })),
      ]);
      setPackages(pkgRes.data.packages || []);
      setCurrencies(currRes.data.currencies || []);
      setOffers(offersRes.data.offers || []);
      setContracts(contractsRes.data.contracts || []);
      setAllocations(allocRes.data.allocations || []);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    }
    setLoading(false);
  }, [corporateToken]);

  const login = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/sponsorship/corporates/login`, loginForm);
      const data = res.data;
      if (data.status === 'pending') {
        toast.info('Account pending admin approval');
        return;
      }
      if (data.status === 'rejected') {
        toast.error('Account has been rejected');
        return;
      }
      localStorage.setItem('corporate_token', data.access_token);
      setCorporateToken(data.access_token);
      setCorporate({ id: data.corporate_id, company_name: data.company_name });
      setIsAuthenticated(true);
      toast.success(`Welcome, ${data.company_name}!`);
      loadDashboardData(data.access_token);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
  };

  const register = async () => {
    try {
      await axios.post(`${API_URL}/api/sponsorship/corporates/register`, registerForm);
      toast.success('Registration submitted! Awaiting admin approval.');
      setAuthMode('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('corporate_token');
    setCorporateToken('');
    setIsAuthenticated(false);
    setCorporate(null);
  };

  const purchasePackage = async (pkgId) => {
    try {
      const res = await axios.post(`${API_URL}/api/sponsorship/packages/purchase`, { package_id: pkgId }, { headers });
      toast.success(res.data.message);
      loadDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    }
  };

  const discoverAthletes = async () => {
    try {
      const params = {};
      if (sportFilter !== 'all') params.sport = sportFilter;
      if (tierFilter !== 'all') params.tier = tierFilter;
      const res = await axios.get(`${API_URL}/api/sponsorship/discover-athletes`, { params, headers });
      setAthletes(res.data.athletes || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Discovery failed');
    }
  };

  const sendOffer = async () => {
    if (!selectedAthlete || !offerForm.category || !offerForm.proposed_value) {
      toast.error('Fill all required fields');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/sponsorship/offers`, {
        athlete_id: selectedAthlete.athlete_id,
        sport: selectedAthlete.sport,
        ...offerForm,
        proposed_value: parseFloat(offerForm.proposed_value),
      }, { headers });
      toast.success('Offer sent!');
      setShowOfferDialog(false);
      loadDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send offer');
    }
  };

  const submitAllocations = async () => {
    const valid = allocForm.filter(a => a.sport && a.allocated_amount);
    if (!valid.length) { toast.error('Add at least one allocation'); return; }
    try {
      await axios.post(`${API_URL}/api/sponsorship/allocations`, {
        allocations: valid.map(a => ({ ...a, allocated_amount: parseFloat(a.allocated_amount) }))
      }, { headers });
      toast.success('Budget allocated!');
      setShowAllocateDialog(false);
      loadDashboardData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Allocation failed');
    }
  };

  useEffect(() => { if (isAuthenticated) discoverAthletes(); }, [sportFilter, tierFilter, isAuthenticated]);

  // AUTH SCREENS
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4" data-testid="sponsorship-auth">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-sm mb-6">
              <ChevronLeft className="w-4 h-4" /> Back to Home
            </Link>
            <Building2 className="w-12 h-12 text-orange-400 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-white">Corporate Sponsorship Portal</h1>
            <p className="text-white/40 text-sm mt-1">Invest in Africa's next generation of champions</p>
          </div>
          <div className="bg-white/[0.03] border border-white/10 rounded-lg p-6">
            <div className="flex mb-6">
              <button onClick={() => setAuthMode('login')} className={`flex-1 py-2 text-sm font-medium border-b-2 transition ${authMode === 'login' ? 'border-orange-400 text-orange-400' : 'border-transparent text-white/40'}`} data-testid="auth-login-tab">Sign In</button>
              <button onClick={() => setAuthMode('register')} className={`flex-1 py-2 text-sm font-medium border-b-2 transition ${authMode === 'register' ? 'border-orange-400 text-orange-400' : 'border-transparent text-white/40'}`} data-testid="auth-register-tab">Register</button>
            </div>
            {authMode === 'login' ? (
              <div className="space-y-4">
                <div>
                  <Label className="text-white/60 text-xs">Email</Label>
                  <Input value={loginForm.contact_email} onChange={e => setLoginForm(p => ({ ...p, contact_email: e.target.value }))} placeholder="corporate@company.com" className="bg-white/5 border-white/10 text-white" data-testid="corp-login-email" />
                </div>
                <div>
                  <Label className="text-white/60 text-xs">Password</Label>
                  <Input type="password" value={loginForm.password} onChange={e => setLoginForm(p => ({ ...p, password: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="corp-login-password" />
                </div>
                <Button onClick={login} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="corp-login-btn">
                  <LogIn className="w-4 h-4 mr-2" /> Sign In
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <Input placeholder="Company Name *" value={registerForm.company_name} onChange={e => setRegisterForm(p => ({ ...p, company_name: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="corp-reg-company" />
                <Input placeholder="Contact Person *" value={registerForm.contact_person} onChange={e => setRegisterForm(p => ({ ...p, contact_person: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="corp-reg-person" />
                <Input placeholder="Email *" value={registerForm.contact_email} onChange={e => setRegisterForm(p => ({ ...p, contact_email: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="corp-reg-email" />
                <Input placeholder="Phone" value={registerForm.contact_phone} onChange={e => setRegisterForm(p => ({ ...p, contact_phone: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
                <Input placeholder="Industry" value={registerForm.industry} onChange={e => setRegisterForm(p => ({ ...p, industry: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
                <Input placeholder="Website" value={registerForm.website} onChange={e => setRegisterForm(p => ({ ...p, website: e.target.value }))} className="bg-white/5 border-white/10 text-white" />
                <Select value={registerForm.country} onValueChange={v => setRegisterForm(p => ({ ...p, country: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['Kenya', 'Uganda', 'Tanzania', 'Rwanda', 'Ethiopia', 'South Africa', 'Nigeria', 'Other'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="password" placeholder="Password *" value={registerForm.password} onChange={e => setRegisterForm(p => ({ ...p, password: e.target.value }))} className="bg-white/5 border-white/10 text-white" data-testid="corp-reg-password" />
                <Button onClick={register} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="corp-reg-btn">
                  <UserPlus className="w-4 h-4 mr-2" /> Register (Pending Approval)
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // CORPORATE DASHBOARD
  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="sponsorship-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0f1923] to-[#0a0a0f] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-white/40 hover:text-white"><ChevronLeft className="w-5 h-5" /></Link>
            <Building2 className="w-5 h-5 text-orange-400" />
            <h1 className="text-lg font-bold text-white">{corporate?.company_name || 'Sponsor Dashboard'}</h1>
          </div>
          <Button variant="outline" size="sm" onClick={logout} className="border-white/10 text-white/60" data-testid="corp-logout-btn">Logout</Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/5 border border-white/10 mb-6">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-orange-500/20 data-[state=active]:text-orange-400 text-white/60" data-testid="corp-tab-dashboard"><PieChart className="w-4 h-4 mr-1.5" /> Dashboard</TabsTrigger>
            <TabsTrigger value="packages" className="data-[state=active]:bg-blue-500/20 data-[state=active]:text-blue-400 text-white/60" data-testid="corp-tab-packages"><Package className="w-4 h-4 mr-1.5" /> Packages</TabsTrigger>
            <TabsTrigger value="discover" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400 text-white/60" data-testid="corp-tab-discover"><Search className="w-4 h-4 mr-1.5" /> Discover Athletes</TabsTrigger>
            <TabsTrigger value="offers" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-400 text-white/60" data-testid="corp-tab-offers"><Send className="w-4 h-4 mr-1.5" /> Offers</TabsTrigger>
            <TabsTrigger value="contracts" className="data-[state=active]:bg-yellow-500/20 data-[state=active]:text-yellow-400 text-white/60" data-testid="corp-tab-contracts"><Shield className="w-4 h-4 mr-1.5" /> Contracts</TabsTrigger>
          </TabsList>

          {/* DASHBOARD */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Sport Allocations', value: allocations.length, icon: Target, color: 'text-emerald-400' },
                { label: 'Pending Offers', value: offers.filter(o => o.status === 'pending').length, icon: Send, color: 'text-purple-400' },
                { label: 'Active Contracts', value: contracts.filter(c => c.status === 'active').length, icon: Shield, color: 'text-yellow-400' },
                { label: 'Athletes Discovered', value: athletes.length, icon: Users, color: 'text-blue-400' },
              ].map((s, i) => (
                <div key={i} className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
                  <s.icon className={`w-5 h-5 ${s.color} mb-2`} />
                  <p className="text-2xl font-bold text-white">{s.value}</p>
                  <p className="text-xs text-white/40">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Sport Allocations */}
            <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-white/60 flex items-center gap-2"><Target className="w-4 h-4 text-emerald-400" /> Sport Budget Allocations</h3>
                <Button size="sm" onClick={() => setShowAllocateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-allocate-budget">
                  <DollarSign className="w-3 h-3 mr-1" /> Allocate Budget
                </Button>
              </div>
              {allocations.length === 0 ? (
                <p className="text-white/30 text-sm py-4 text-center">No allocations yet. Purchase a package and allocate budget to sports.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {allocations.map(a => (
                    <div key={a.sport} className="bg-white/5 rounded p-3">
                      <div className="text-white text-sm font-medium">{a.sport}</div>
                      <div className="text-emerald-400 text-lg font-bold">{a.allocated_amount?.toLocaleString()} <span className="text-xs text-white/30">{a.currency_code}</span></div>
                      <div className="text-white/30 text-xs">Remaining: {a.remaining_amount?.toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recent Contracts */}
            <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4">
              <h3 className="text-sm font-medium text-white/60 flex items-center gap-2 mb-3"><Shield className="w-4 h-4 text-yellow-400" /> Active Contracts</h3>
              {contracts.filter(c => c.status === 'active').length === 0 ? (
                <p className="text-white/30 text-sm py-4 text-center">No active contracts yet.</p>
              ) : (
                <div className="space-y-2">
                  {contracts.filter(c => c.status === 'active').map(c => (
                    <div key={c.id} className="flex items-center justify-between bg-white/5 rounded p-3">
                      <div>
                        <span className="text-white text-sm font-medium">{c.athlete_name}</span>
                        <span className="text-white/30 text-xs ml-2">{c.sport} — {c.category}</span>
                      </div>
                      <span className="text-yellow-400 font-bold text-sm">{c.contract_value?.toLocaleString()} {c.currency}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* PACKAGES */}
          <TabsContent value="packages">
            <div className="grid md:grid-cols-3 gap-4" data-testid="packages-grid">
              {packages.map(p => (
                <div key={p.id} className={`bg-white/[0.03] border rounded-lg p-6 ${p.package_name === 'Gold' ? 'border-yellow-500/30' : p.package_name === 'Silver' ? 'border-gray-300/30' : 'border-amber-700/30'}`}>
                  <div className="text-center mb-4">
                    <Trophy className={`w-8 h-8 mx-auto mb-2 ${p.package_name === 'Gold' ? 'text-yellow-400' : p.package_name === 'Silver' ? 'text-gray-300' : 'text-amber-600'}`} />
                    <h3 className="text-white font-bold text-lg">{p.package_name}</h3>
                    <p className="text-white/40 text-xs mt-1">{p.description}</p>
                  </div>
                  <div className="text-center mb-4">
                    <span className="text-3xl font-bold text-white">{p.amount?.toLocaleString()}</span>
                    <span className="text-white/40 text-sm ml-1">{p.currency_code}</span>
                  </div>
                  <div className="text-xs text-white/30 text-center mb-4">
                    Platform fee: {p.platform_fee_percentage}% | Allocatable: {(p.amount * (1 - p.platform_fee_percentage / 100)).toLocaleString()} {p.currency_code}
                  </div>
                  <Button onClick={() => purchasePackage(p.id)} className="w-full bg-orange-600 hover:bg-orange-700" data-testid={`buy-pkg-${p.package_name.toLowerCase()}`}>
                    Purchase {p.package_name}
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* DISCOVER ATHLETES */}
          <TabsContent value="discover">
            <div className="flex gap-3 mb-4">
              <Select value={sportFilter} onValueChange={v => setSportFilter(v)}>
                <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white" data-testid="discover-sport-filter"><SelectValue placeholder="All Sports" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Allocated Sports</SelectItem>
                  {allocations.map(a => <SelectItem key={a.sport} value={a.sport}>{a.sport}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={tierFilter} onValueChange={v => setTierFilter(v)}>
                <SelectTrigger className="w-[180px] bg-white/5 border-white/10 text-white" data-testid="discover-tier-filter"><SelectValue placeholder="All Tiers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  {Object.entries(TIER_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={discoverAthletes} variant="outline" className="border-white/10 text-white/60" data-testid="btn-discover"><Search className="w-4 h-4 mr-1" /> Search</Button>
            </div>
            {athletes.length === 0 ? (
              <div className="text-center py-12 bg-white/[0.02] border border-white/5 rounded-lg">
                <Users className="w-10 h-10 text-white/10 mx-auto mb-2" />
                <p className="text-white/40 text-sm">No eligible athletes found. Allocate budget to sports first.</p>
              </div>
            ) : (
              <div className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden" data-testid="discover-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/5 text-white/40 text-xs">
                      <th className="px-4 py-3 text-left">Rank</th>
                      <th className="px-4 py-3 text-left">Athlete</th>
                      <th className="px-4 py-3 text-left hidden md:table-cell">Sport</th>
                      <th className="px-4 py-3 text-left hidden md:table-cell">School</th>
                      <th className="px-4 py-3 text-right">Points</th>
                      <th className="px-4 py-3 text-center">Tier</th>
                      <th className="px-4 py-3 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {athletes.map(a => (
                      <tr key={a.athlete_id + a.sport} className="border-b border-white/5 hover:bg-white/[0.03]">
                        <td className="px-4 py-3 text-white/50 text-sm">#{a.rank_position}</td>
                        <td className="px-4 py-3 text-white text-sm font-medium">{a.athlete_name}</td>
                        <td className="px-4 py-3 text-white/50 text-sm hidden md:table-cell">{a.sport}</td>
                        <td className="px-4 py-3 text-white/40 text-sm hidden md:table-cell">{a.school || '—'}</td>
                        <td className="px-4 py-3 text-right text-white font-bold text-sm">{a.total_points?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded border ${TIER_COLORS[a.sponsorship_tier] || ''}`}>{TIER_LABELS[a.sponsorship_tier] || '—'}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button size="sm" variant="outline" className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 text-xs" onClick={() => { setSelectedAthlete(a); setShowOfferDialog(true); }} data-testid={`offer-btn-${a.athlete_id}`}>
                            <Send className="w-3 h-3 mr-1" /> Offer
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* OFFERS */}
          <TabsContent value="offers">
            {offers.length === 0 ? (
              <div className="text-center py-12 bg-white/[0.02] border border-white/5 rounded-lg">
                <Send className="w-10 h-10 text-white/10 mx-auto mb-2" />
                <p className="text-white/40 text-sm">No offers sent yet.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="offers-list">
                {offers.map(o => (
                  <div key={o.id} className="bg-white/[0.03] border border-white/5 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-sm">{o.athlete_name}</span>
                        <span className="text-xs bg-white/5 text-white/50 px-2 py-0.5 rounded">{o.sport}</span>
                        <span className="text-xs text-white/30">{o.category}</span>
                      </div>
                      {o.message && <p className="text-white/30 text-xs mt-1">{o.message}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-orange-400 font-bold text-sm">{o.proposed_value?.toLocaleString()} {o.currency_code}</span>
                      <span className={`text-xs px-2 py-1 rounded ${o.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : o.status === 'accepted' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                        {o.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* CONTRACTS */}
          <TabsContent value="contracts">
            {contracts.length === 0 ? (
              <div className="text-center py-12 bg-white/[0.02] border border-white/5 rounded-lg">
                <Shield className="w-10 h-10 text-white/10 mx-auto mb-2" />
                <p className="text-white/40 text-sm">No contracts yet. Send offers to athletes to create contracts.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="contracts-list">
                {contracts.map(c => (
                  <div key={c.id} className="bg-white/[0.03] border border-white/5 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-white font-medium">{c.athlete_name}</span>
                        <span className="text-white/30 text-sm ml-2">{c.sport} — {c.category}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${c.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/10 text-white/40'}`}>{c.status}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-white/40">
                      <span>Value: <span className="text-yellow-400 font-bold">{c.contract_value?.toLocaleString()} {c.currency}</span></span>
                      <span>Start: {c.start_date?.split('T')[0]}</span>
                      <span>End: {c.end_date?.split('T')[0]}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Offer Dialog */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5 text-orange-400" /> Send Sponsorship Offer</DialogTitle>
          </DialogHeader>
          {selectedAthlete && (
            <div className="bg-white/5 rounded p-3 mb-3">
              <div className="text-white font-medium">{selectedAthlete.athlete_name}</div>
              <div className="text-white/40 text-xs">{selectedAthlete.sport} — Rank #{selectedAthlete.rank_position} — {selectedAthlete.total_points?.toLocaleString()} pts</div>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label className="text-white/60 text-xs">Category *</Label>
              <Select value={offerForm.category} onValueChange={v => setOfferForm(p => ({ ...p, category: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="offer-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.filter(c => !(selectedAthlete?.sponsored_categories || []).includes(c)).map(c => (
                    <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Value *</Label>
                <Input type="number" value={offerForm.proposed_value} onChange={e => setOfferForm(p => ({ ...p, proposed_value: e.target.value }))} placeholder="50000" className="bg-white/5 border-white/10 text-white" data-testid="offer-value" />
              </div>
              <div>
                <Label className="text-white/60 text-xs">Currency</Label>
                <Select value={offerForm.currency_code} onValueChange={v => setOfferForm(p => ({ ...p, currency_code: v }))}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => <SelectItem key={c.currency_code} value={c.currency_code}>{c.currency_symbol} {c.currency_code}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-white/60 text-xs">Duration</Label>
              <Select value={String(offerForm.duration_months)} onValueChange={v => setOfferForm(p => ({ ...p, duration_months: parseInt(v) }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[6, 12, 18, 24, 36].map(m => <SelectItem key={m} value={String(m)}>{m} months</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Textarea placeholder="Message to athlete (optional)" value={offerForm.message} onChange={e => setOfferForm(p => ({ ...p, message: e.target.value }))} className="bg-white/5 border-white/10 text-white" rows={2} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOfferDialog(false)} className="border-white/10 text-white/60">Cancel</Button>
            <Button onClick={sendOffer} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-send-offer"><Send className="w-4 h-4 mr-1" /> Send Offer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocate Dialog */}
      <Dialog open={showAllocateDialog} onOpenChange={setShowAllocateDialog}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Target className="w-5 h-5 text-emerald-400" /> Allocate Budget to Sports</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {allocForm.map((a, i) => (
              <div key={i} className="flex gap-2">
                <Select value={a.sport} onValueChange={v => { const n = [...allocForm]; n[i].sport = v; setAllocForm(n); }}>
                  <SelectTrigger className="flex-1 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Sport" /></SelectTrigger>
                  <SelectContent>
                    {['Football','Basketball','Athletics','Tennis','Swimming','Rugby','Volleyball','Hockey','Netball','Handball','Table Tennis','Badminton','Chess','Karate','Judo','Taekwondo','Padel','Esports','Motor Sports','Squash'].map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="number" placeholder="Amount" value={a.allocated_amount} onChange={e => { const n = [...allocForm]; n[i].allocated_amount = e.target.value; setAllocForm(n); }} className="w-32 bg-white/5 border-white/10 text-white" />
                {allocForm.length > 1 && (
                  <Button size="icon" variant="ghost" onClick={() => setAllocForm(allocForm.filter((_, j) => j !== i))} className="text-red-400"><X className="w-4 h-4" /></Button>
                )}
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setAllocForm([...allocForm, { sport: '', allocated_amount: '', currency_code: 'KES' }])} className="border-white/10 text-white/60 w-full">
              + Add Sport
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAllocateDialog(false)} className="border-white/10 text-white/60">Cancel</Button>
            <Button onClick={submitAllocations} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirm-allocate">Allocate Budget</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
